import { forwardRef } from 'react';
import PropTypes from 'prop-types';
import ReactSelect from 'react-select';
import Creatable from 'react-select/creatable';
import cln from 'classnames';
import { MultiValueContainer, DropdownIndicator, IndicatorSeparator, MultiValueLabel, ControlComponent } from './Components';
import InputError from '../InputError';
import './Select.module.scss';

/**
 * This component is a wrapper for [react-select](https://github.com/JedWatson/react-select). As of today we support regular and creatable variant in this single component.
 * If you want to use creatable variant you don't need to import another component (like it is described in react-select docs), you just need
 * to provide `creatable` prop set to true and use all additional props from `creatable` variant described in react-select docs.
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const Select = /*#__PURE__*/forwardRef(function Select({
  value = null,
  size = 'big',
  label = '',
  // eslint-disable-line
  splitButton = true,
  className,
  width,
  // eslint-disable-line
  testId,
  error,
  highlighted = false,
  components = {},
  isMulti = false,
  creatable = false,
  ...rest
}, ref) {
  const selectClass = cln('sru_Select', className, {
    [`sru_Select--${size}`]: size,
    'sru_Select--split': splitButton,
    'sru_Select--error': error,
    'sru_Select--highlighted': highlighted
  });
  const SelectContainer = creatable ? Creatable : ReactSelect;
  return /*#__PURE__*/_jsxs("div", {
    "data-testid": testId,
    children: [/*#__PURE__*/_jsx(SelectContainer, {
      value: value,
      className: selectClass,
      classNamePrefix: "sru_Select",
      ref: ref,
      components: {
        MultiValueContainer,
        DropdownIndicator,
        IndicatorSeparator,
        MultiValueLabel,
        Control: ControlComponent,
        ...components
      },
      isMulti: isMulti,
      ...rest
    }), error && /*#__PURE__*/_jsx(InputError, {
      children: error
    })]
  });
});
Select.propTypes = {
  /** Current value of the control */
  value: PropTypes.any,
  // eslint-disable-line react/forbid-prop-types
  /** Support multiple selected options */
  isMulti: PropTypes.bool,
  /** One of predefined sizes */
  size: PropTypes.oneOf(['big', 'small', 'compact']),
  /** Indicates whether the arrow icon should be split or not */
  splitButton: PropTypes.bool,
  /** @ignore */
  components: PropTypes.object,
  /** Width of the select container in px. Keep in mind that this will apply an inline style. */
  width: PropTypes.number,
  /** Label which will be displayed above the input field */
  label: PropTypes.string,
  /** Custom class name set on the input container */
  className: PropTypes.string,
  /** Error string which should be displayed below the input field. */
  error: PropTypes.string,
  /** Whether or not the component should be highlighted */
  highlighted: PropTypes.bool,
  /** Allow adding new element to the list  */
  creatable: PropTypes.bool,
  /** @ignore */
  testId: PropTypes.string
};
export default Select;